import { useState } from "react";
import { siteInfo } from "@/mocks/content";
import Breadcrumb from "@/components/feature/Breadcrumb";
import useDocumentMeta from "@/hooks/useDocumentMeta";

const FORM_ADDR = "https://readdy.ai/api/form/dailqkrp14a1h3mr76jg";

const channels = [
  { icon: "ri-mail-send-line", label: "电子邮箱", value: siteInfo.email, desc: "内容纠错与线索反馈优先通过邮件处理" },
  { icon: "ri-time-line", label: "更新时间", value: siteInfo.workTime, desc: "留言一般在 1 个工作日内回复" },
  { icon: "ri-chat-smile-2-line", label: "留言反馈", value: "右侧表单", desc: "描述尽量具体，方便我们快速定位问题" },
];

export default function ContactPage() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  useDocumentMeta({
    title: "联系我们 - 看料91 反馈与线索提交",
    description:
      "联系看料91团队，提交内容纠错、话题线索或合作咨询。填写联系表单即可反馈，我们会在一个工作日内回复，帮助你更顺畅地翻看与阅读内容。",
    keywords: "看料91,联系我们,看料91反馈,看料91浏览,内容线索",
    path: "/contact",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "ContactPage",
      name: "联系我们 - 看料91",
      url: "https://fxwlkj.cn/contact",
      inLanguage: "zh-CN",
    },
  });

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setFormError("");
      form.reset();
      return;
    }

    setStatus("submitting");
    setFormError("");

    const params = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      const str = String(value).trim();
      if (str) params.append(key, str);
    });

    try {
      const response = await fetch(FORM_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");
      const isOk = response.ok && parsed?.code === "OK" && !isSpam;

      if (isOk) {
        setStatus("success");
        setFormError("");
        form.reset();
      } else {
        setStatus("error");
        setFormError(
          typeof serverMsg === "string" && serverMsg
            ? serverMsg
            : "提交失败，请稍后重试，或直接通过邮箱联系我们。"
        );
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交未成功，请稍后重试。");
    }
  };

  return (
    <>
      <Breadcrumb items={[{ label: "联系我们" }]} />

      <section className="w-full px-4 md:px-8">
        <div className="max-w-3xl">
          <span className="text-xs md:text-sm text-primary-700 font-medium">联系我们</span>
          <h1 className="font-heading text-3xl md:text-5xl font-black text-foreground-950 mt-2 leading-snug">
            有问题、有线索，直接告诉我们
          </h1>
          <p className="text-sm md:text-base text-foreground-700 mt-5 leading-loose">
            无论是内容纠错、话题线索，还是想聊聊使用体验，都可以通过下面的表单或邮箱联系到我们。描述越具体，处理速度越快。
          </p>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 py-12 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5 flex flex-col gap-4">
            {channels.map((c) => (
              <div key={c.label} className="flex items-start gap-4 bg-background-100 rounded-lg p-5">
                <span className="w-11 h-11 flex-shrink-0 flex items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                  <i className={`${c.icon} text-xl`} />
                </span>
                <div>
                  <span className="block text-xs text-foreground-500">{c.label}</span>
                  <span className="block font-heading text-base font-bold text-foreground-950 mt-0.5 break-all">
                    {c.value}
                  </span>
                  <span className="block text-xs text-foreground-500 mt-1.5 leading-relaxed">{c.desc}</span>
                </div>
              </div>
            ))}
          </div>

          <div className="lg:col-span-7">
            <div className="bg-background-100 rounded-lg p-5 md:p-8">
              <h2 className="font-heading text-xl md:text-2xl font-black text-foreground-950">
                留下你的信息
              </h2>
              <p className="text-sm text-foreground-600 mt-2 mb-6">
                带 <span className="text-primary-600">*</span> 的为必填项。
              </p>

              <form data-readdy-form onSubmit={handleSubmit} className="flex flex-col gap-5" noValidate>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="name" className="block text-sm text-foreground-800 mb-2">
                      称呼 <span className="text-primary-600">*</span>
                    </label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      required
                      placeholder="怎么称呼你"
                      className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-md text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm text-foreground-800 mb-2">
                      邮箱 <span className="text-primary-600">*</span>
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      placeholder="用于接收回复"
                      className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-md text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="topic" className="block text-sm text-foreground-800 mb-2">
                    反馈类型
                  </label>
                  <select
                    id="topic"
                    name="topic"
                    defaultValue="内容纠错"
                    className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-md text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400"
                  >
                    <option value="内容纠错">内容纠错</option>
                    <option value="话题线索">话题线索</option>
                    <option value="使用建议">使用建议</option>
                    <option value="合作咨询">合作咨询</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm text-foreground-800 mb-2">
                    详细说明 <span className="text-primary-600">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    placeholder="请描述具体问题或线索，最多 500 字"
                    className="w-full px-4 py-3 text-sm bg-background-50 border border-background-300 rounded-md text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 resize-none"
                  />
                </div>

                <input
                  type="text"
                  name="website_alt"
                  className="contact-field-alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />

                <div className="flex flex-col sm:flex-row items-center gap-4">
                  <button
                    type="submit"
                    disabled={status === "submitting"}
                    className="w-full sm:w-auto px-6 py-3 bg-primary-500 text-background-50 rounded-md text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {status === "submitting" ? "提交中…" : "提交反馈"}
                  </button>

                  {status === "success" && (
                    <p className="text-sm text-secondary-700 flex items-center gap-1.5">
                      <span className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-checkbox-circle-line text-lg" />
                      </span>
                      已收到你的反馈，我们会尽快查看。
                    </p>
                  )}
                  {status === "error" && (
                    <p className="text-sm text-primary-700 flex items-start gap-1.5">
                      <span className="w-5 h-5 flex items-center justify-center mt-0.5">
                        <i className="ri-error-warning-line text-lg" />
                      </span>
                      {formError}
                    </p>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}